import { Box, Input, Tag, TagCloseButton, TagLabel } from "@chakra-ui/react";
import React, { forwardRef, Ref, useState } from "react";

interface KeywordsInputProps {
  keywords: string[];
  onChange: (keywords: string[]) => void;
  placeholder?: string;
}

const KeywordsInput = forwardRef<HTMLInputElement, KeywordsInputProps>(
  ({ keywords, onChange, placeholder }, ref: Ref<HTMLInputElement>) => {
    const [inputValue, setInputValue] = useState("");

    const handleEnter = (): void => {
      const lowercasedValue = inputValue.trim().toLowerCase();
      if (lowercasedValue) {
        setInputValue("");
        const newKeywords = lowercasedValue.includes(",")
          ? lowercasedValue
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : [lowercasedValue];
        const newList = [...keywords, ...newKeywords];
        onChange([...new Set(newList)]);
      }
    };

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ): void => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleEnter();
      }
    };

    const handleRemoveKeyword = (keyword: string): void => {
      const newList = keywords.filter((k) => k !== keyword);
      onChange(newList);
    };

    const handleBlur = (): void => {
      const lowercasedValue = inputValue.trim().toLowerCase();
      if (lowercasedValue) {
        handleEnter();
      }
    };

    return (
      <Box
        backgroundColor="white"
        borderWidth={keywords.length ? "1px" : "0"}
        borderColor="gray.200"
        borderRadius="6px"
        role="region"
        aria-label="Keywords input"
      >
        {!!keywords.length && (
          <Box mb={2} px={3}>
            {keywords.map((keyword) => (
              <Tag
                key={keyword}
                size="md"
                variant="solid"
                backgroundColor="blue.50"
                color="blue.700"
                fontWeight="400"
                borderRadius="4px"
                mr={2}
                mt={2}
                role="listitem"
              >
                <TagLabel aria-label={`Keyword: ${keyword}`}>
                  {keyword}
                </TagLabel>
                <TagCloseButton onClick={() => handleRemoveKeyword(keyword)} />
              </Tag>
            ))}
          </Box>
        )}
        <Input
          ref={ref}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Type keywords and press Enter"}
          borderColor={keywords.length ? "transparent" : "gray.200"}
          borderBottomLeftRadius="6px"
          borderBottomRightRadius="6px"
          borderTopLeftRadius={keywords.length ? "0" : "6px"}
          borderTopRightRadius={keywords.length ? "0" : "6px"}
          _hover={{
            borderColor: keywords.length ? "transparent" : "gray.200",
          }}
          _placeholder={{
            color: "gray.400",
          }}
        />
      </Box>
    );
  }
);

export default KeywordsInput;
